/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef, useEffect, useMemo } from "react";

/** Utilities */
import formatDate from "../../lib/formatDate";
import formatActualHours from "../../lib/formatActualHours";
import { encryptUrlData } from "../../services/CryptoService";

/** Contexts */
import { useGroupBy } from "../../utils/Context/TaskGroupByContext";

/** Common Components */
import IconButton from "../ui/iconbutton/IconButton";
import Checkbox from "./elementalUI/checkbox/Checkbox";
import Iconify from "../../layouts/components/iconify/iconify";

/** Common Task Grid Components */
import TaskGridSkeleton from "./NewTaskGridSkeleton";
import TaskGridBulkBar from "./TaskGridBulkBar";
import { getTaskGridColumns, TASK_SORT_ORDERS } from "./taskGridColumns";
import { normalizeTaskGridRows } from "./adapters/normalizeTaskGridRow";
import StatusSelect from "./elementalUI/statusselect/StatusSelect";
import AssigneeSelect from "./elementalUI/assigneeselect/AssigneeSelect";
import PrioritySelect from "./elementalUI/priorityselect/PrioritySelect";
import TaskTypeSelect from "./elementalUI/tasktypeselect/TaskTypeSelect";
import NoDataFound from "../ui/noDataFound/NoDataFound";

/** Common Services & Data Files */
import SwalAlert from "../../services/swalService/SwalService";
import ToastService from "../../services/toastService/ToastService";

/** RTK Query */
import { useDeleteTaskMutation, useBulkUpdateTasksMutation, useBulkDeleteTasksMutation } from "../../app/services/taskAPI";
import { useUpdateBacklogTaskMutation } from "../../app/services/backLogAPI";

/** CSS Files */
import "./NewTaskGrid.scss";
import Buttons from "../ui/button/Buttons";

export const TASK_GRID_SELECTION_MODE = {
  BULK: "bulk",
  PLANNING: "planning",
};

const TaskGridStats = ({ totalEstHours, totalTask, totalBug }) => (
  <div className="group-stats">
    <div className="est-hours" title="Estimated hours">
      <Iconify icon="ri:timer-line" width={14} />
      <span>Est. {totalEstHours} hrs</span>
    </div>
    <div className="task-count" title="Tasks">
      <Iconify icon="ic:outline-task-alt" width={14} />
      <span>{totalTask} {totalTask === 1 ? "Task" : "Tasks"}</span>
    </div>
    <div className="bug-count" title="Bugs">
      <Iconify icon="codicon:bug" width={14} />
      <span>{totalBug} {totalBug === 1 ? "Bug" : "Bugs"}</span>
    </div>
  </div>
);

const BULK_SELECTION_CAP = 50;
const BULK_SELECTION_CAP_MESSAGE = "You can bulk update up to 50 tasks at a time.";

const NewTaskGrid = (props) => {

  const {
    isLoading,
    isRefreshing = false,
    isError = false,
    onRetry,
    onClearFilters,
    hasActiveFilters = false,
    hasActiveSearch = false,
    userList,
    statusList,
    priorityList,
    taskTypeList = [],
    taskListData,
    allowEditStatus,
    allowEditAssignee,
    projectId,
    onRefreshGrid,
    isTeamPlanning,
    isShowActualTime = false,
    isShowRemainingTime = false,
    onRemoveTask,
    customGridActions,
    onActionClick,
    totalCount = null,
    selectionMode = TASK_GRID_SELECTION_MODE.BULK,
    onSelectionChange,
  } = props;

  const isBulkSelectionMode = selectionMode === TASK_GRID_SELECTION_MODE.BULK;
  // Planning Save sends remTime as assigned hours; allow local edit so planners can
  // allocate less than the full task estimate against member capacity.
  const canEditRemainingTime =
    isShowRemainingTime && selectionMode === TASK_GRID_SELECTION_MODE.PLANNING;

  const taskInputRef = useRef(null);
  const timeInputRef = useRef(null);
  const remTimeInputRef = useRef(null);
  const skipTitleBlurRef = useRef(false);
  const skipTimeBlurRef = useRef(false);
  const skipRemTimeBlurRef = useRef(false);
  const containerRef = useRef(null);
  const toolbarRef = useRef(null);
  const exceededBulkCapRef = useRef(false);

  const [taskList, setTaskList] = useState(() => normalizeTaskGridRows(taskListData || []));
  const [selectedRows, setSelectedRows] = useState([]);
  const [closeOnSelect, setCloseOnSelect] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editingTaskTimeId, setEditingTaskTimeId] = useState(null);
  const [editingTaskRemId, setEditingTaskRemId] = useState(null);
  const [taskTitle, setTaskTitle] = useState("");
  const [taskEstTime, setTaskEstTime] = useState(null);
  const [taskRemTime, setTaskRemTime] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [savingTaskIds, setSavingTaskIds] = useState(() => new Set());
  const [isBulkApplying, setIsBulkApplying] = useState(false);
  const {
    selectedGroupBy,
    selectedSort,
    selectedOrder,
    setSelectedSort,
    setSelectedOrder,
  } = useGroupBy();

  const isGrouped = Boolean(selectedGroupBy?.value);

  const gridColumns = useMemo(
    () => getTaskGridColumns(isShowActualTime, isShowRemainingTime),
    [isShowActualTime, isShowRemainingTime]
  );

  // Selection is looked up once per row on every render, so keep it O(1) instead
  // of scanning the selected array for each row.
  const selectedTaskIdSet = useMemo(
    () => new Set(selectedRows.map((task) => task.taskId)),
    [selectedRows]
  );

  // Totals for the toolbar chips. These stay across the whole list even when a
  // Group By is active, where each group header shows its own subtotals.
  const overallStats = useMemo(() => {
    const tasks = taskList || [];

    return {
      totalEstHours: tasks
        .reduce((total, task) => total + Number(task.estimateHours || 0), 0)
        .toFixed(2),
      totalTask: tasks.filter((task) => task.taskTypeId === 1).length,
      totalBug: tasks.filter((task) => task.taskTypeId === 2).length,
    };
  }, [taskList]);

  const { confirm } = SwalAlert();

  const [updateBacklogTask] = useUpdateBacklogTaskMutation();
  const [deleteTask] = useDeleteTaskMutation();
  const [bulkUpdateTasks] = useBulkUpdateTasksMutation();
  const [bulkDeleteTasks] = useBulkDeleteTasksMutation();

  useEffect(() => {
    if (!taskListData) return;

    const normalized = normalizeTaskGridRows(taskListData);
    setTaskList(normalized);

    // A refetch (sort, filter, search) can drop rows that are still selected, and
    // a bulk action would then hit tasks the user can no longer see.
    const availableIds = new Set(normalized.map((task) => task.taskId));
    setSelectedRows((prev) => {
      const next = prev.filter((task) => availableIds.has(task.taskId));
      return next.length === prev.length ? prev : next;
    });
  }, [taskListData]);

  // Planning pages own Save outside the grid; keep them in sync with checkbox state.
  useEffect(() => {
    onSelectionChange?.(selectedRows);
  }, [selectedRows]);

  // Group headers stick directly below the toolbar, whose height changes when the
  // bulk controls wrap, so the offset is measured instead of hard coded.
  useEffect(() => {
    const container = containerRef.current;
    const toolbar = toolbarRef.current;

    if (!container || !toolbar || typeof ResizeObserver === "undefined") return;

    const syncToolbarHeight = () => {
      const { height } = toolbar.getBoundingClientRect();
      container.style.setProperty("--task-grid-toolbar-height", `${Math.round(height)}px`);
    };

    syncToolbarHeight();

    const observer = new ResizeObserver(syncToolbarHeight);
    observer.observe(toolbar);

    return () => observer.disconnect();
  }, [isLoading]);

  const updatedGroupedTaskList = useMemo(() => {
    if (!selectedGroupBy?.value) {
      const tasks = taskList || [];

      const totalEstHours = tasks.reduce((total, task) => {
        return total + Number(task.estimateHours || 0);
      }, 0);

      const totalTask = tasks.reduce((total, task) => total + (task.taskTypeId === 1 ? 1 : 0), 0);
      const totalBug = tasks.reduce((total, task) => total + (task.taskTypeId === 2 ? 1 : 0), 0);

      return {
        All: {
          tasks,
          totalEstHours: totalEstHours.toFixed(2),
          totalTask,
          totalBug,
        },
      };
    }

    const grouped = {};

    (taskList || []).forEach((task) => {
      const key = task[selectedGroupBy.value] || "Undefined";

      if (!grouped[key]) {
        grouped[key] = {
          tasks: [],
          totalEstHours: 0,
          totalTask: 0,
          totalBug: 0,
        };
      }

      grouped[key].tasks.push(task);
      grouped[key].totalEstHours += Number(task.estimateHours || 0);
      if (task.taskTypeId === 1) grouped[key].totalTask += 1;
      if (task.taskTypeId === 2) grouped[key].totalBug += 1;
    });

    const sortedTaskArray = Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));

    return Object.fromEntries(
      sortedTaskArray.map(([key, value]) => [
        key,
        {
          ...value,
          totalEstHours: value.totalEstHours.toFixed(2),
        },
      ])
    );
  }, [taskList, selectedGroupBy]);

  useEffect(() => {
    setCollapsedGroups(prev => {
      const updated = { ...prev };

      Object.keys(updatedGroupedTaskList).forEach(key => {
        if (!(key in updated)) {
          updated[key] = true;
        }
      });
      return updated;
    });
  }, [updatedGroupedTaskList]);

  const allExpanded = Object.keys(collapsedGroups).length > 0 && Object.values(collapsedGroups).every(v => v === true);

  const toggleGroupCollapse = (groupKey) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  const toggleAllGroups = () => {
    const shouldExpand = !allExpanded;
    const newState = {};
    Object.keys(updatedGroupedTaskList).forEach(key => {
      newState[key] = shouldExpand;
    });
    setCollapsedGroups(newState);
  };

  const sortDirection = selectedOrder?.value === "DESC" ? "DESC" : "ASC";

  const isSortedByColumn = (column) => (
    Boolean(column.sortKey) && selectedSort?.value === column.sortKey
  );

  // Sorting runs on the server, so this writes to the same context the Sort
  // dropdown uses and the list refetches sorted across every task, not just the
  // rows already loaded.
  const handleColumnSort = (column) => {
    if (!column.sortKey || !setSelectedSort || !setSelectedOrder) return;

    const isActive = isSortedByColumn(column);

    if (!isActive) {
      setSelectedSort({ label: column.sortLabel, value: column.sortKey });
    }

    setSelectedOrder(
      isActive && sortDirection === "ASC" ? TASK_SORT_ORDERS.DESC : TASK_SORT_ORDERS.ASC
    );
  };

  const setTaskSaving = (taskId, isSaving) => {
    setSavingTaskIds((prev) => {
      const next = new Set(prev);
      if (isSaving) {
        next.add(taskId);
      } else {
        next.delete(taskId);
      }
      return next;
    });
  };

  const persistTaskPatch = async (taskId, patch, request) => {
    const previous = taskList.find((task) => task.taskId === taskId);
    if (!previous) return;

    setTaskList((prev) => prev.map((task) => (
      task.taskId === taskId ? { ...task, ...patch } : task
    )));
    setTaskSaving(taskId, true);

    try {
      await updateBacklogTask(request).unwrap();
    } catch (error) {
      setTaskList((prev) => prev.map((task) => (
        task.taskId === taskId ? previous : task
      )));
      ToastService.error("Could not update task");
    } finally {
      setTaskSaving(taskId, false);
    }
  };

  const handleTaskNavigate = (task) => {
    const dataUrl = {
      taskId: task.taskId,
      projectId: projectId,
    };
    window.open(`/project/EditTask/${encryptUrlData(dataUrl)}`, "_blank");
  };

  const handleDeleteTask = (task) => {
    confirm(
      "Delete?",
      "Are you sure you want to Delete?",
      "Delete",
      "Cancel"
    ).then(async (confirmed) => {
      if (!confirmed) return;

      const previousList = taskList;
      const previousSelected = selectedRows;
      setTaskList((prev) => prev.filter((item) => item.taskId !== task.taskId));
      setSelectedRows((prev) => prev.filter((item) => item.taskId !== task.taskId));
      setTaskSaving(task.taskId, true);

      try {
        const result = await deleteTask(task.taskId).unwrap();
        ToastService.success(result?.errorMessage || "Task deleted");
      } catch (error) {
        setTaskList(previousList);
        setSelectedRows(previousSelected);
        ToastService.error("Could not delete task");
      } finally {
        setTaskSaving(task.taskId, false);
      }
    });
  };

  const handleRemoveTaskFromPlanning = (task) => {
    onRemoveTask?.(task)
  }

  const handleCustomActionClick = (action, task) => {
    onActionClick?.[action]?.(action, task)
  }

  const handleTaskCopyClick = async (task) => {
    const textToCopy = `${task.projectCode}-${task.taskId} ${task.title}`;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(textToCopy);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = textToCopy;
        textArea.setAttribute("readonly", "");
        textArea.style.position = "absolute";
        textArea.style.left = "-9999px";
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      ToastService.info("Task Detail Copied to Clipboard!");
    } catch (error) {
      ToastService.error("Could not copy task details");
    }
  }

  const handleTaskEditClick = (task, fieldType) => {
    if (fieldType === "title") {
      setEditingTaskId(task.taskId);
      setTaskTitle(task.title);
      setTimeout(() => taskInputRef.current?.focus(), 0);
    } else if (fieldType === "time") {
      setEditingTaskTimeId(task.taskId);
      setTaskEstTime(task.estimateHours);
      setTimeout(() => timeInputRef.current?.focus(), 0);
    } else if (fieldType === "remTime" && canEditRemainingTime) {
      setEditingTaskRemId(task.taskId);
      setTaskRemTime(task.remTime ?? task.remainingHours ?? 0);
      setTimeout(() => remTimeInputRef.current?.focus(), 0);
    }
  };

  const cancelTitleEdit = () => {
    setEditingTaskId(null);
    setTaskTitle("");
  };

  const cancelTimeEdit = () => {
    setEditingTaskTimeId(null);
    setTaskEstTime("");
  };

  const cancelRemTimeEdit = () => {
    setEditingTaskRemId(null);
    setTaskRemTime("");
  };

  const handleSaveTaskTitle = (taskId) => {
    const currentTask = taskList.find((task) => task.taskId === taskId);
    const nextTitle = (taskTitle || "").trim();

    cancelTitleEdit();

    if (!currentTask || nextTitle === currentTask.title || nextTitle === "") {
      return;
    }

    persistTaskPatch(taskId, { title: nextTitle }, { taskId, title: nextTitle });
  };

  const handleTaskTitleChange = (e) => {
    setTaskTitle(e.target.value);
  };

  const handleTaskTimeChange = (e) => {
    setTaskEstTime(e.target.value);
  };

  const handleTaskRemTimeChange = (e) => {
    setTaskRemTime(e.target.value);
  };

  const handleSaveTaskTime = (taskId) => {
    const currentTask = taskList.find((task) => task.taskId === taskId);
    const nextHours = Number(taskEstTime);

    if (!Number.isFinite(nextHours) || nextHours < 0) {
      ToastService.error("Enter a valid estimate of 0 or more");
      return;
    }

    cancelTimeEdit();

    if (!currentTask || Number(currentTask.estimateHours) === nextHours) {
      return;
    }

    persistTaskPatch(taskId, { estimateHours: nextHours }, { taskId, estimateHours: nextHours });
  };

  // Local-only: planning Save payload uses remTime as assigned hours for this cycle.
  // Do not call the backlog update API here.
  const handleSaveTaskRemTime = (taskId) => {
    const currentTask = taskList.find((task) => task.taskId === taskId);
    const nextHours = Number(taskRemTime);

    if (!Number.isFinite(nextHours) || nextHours < 0) {
      ToastService.error("Enter a valid remaining time of 0 or more");
      return;
    }

    cancelRemTimeEdit();

    if (!currentTask) {
      return;
    }

    const currentHours = Number(currentTask.remTime ?? currentTask.remainingHours ?? 0);
    if (currentHours === nextHours) {
      return;
    }

    const remPatch = { remTime: nextHours, remainingHours: nextHours };

    setTaskList((prev) => prev.map((task) => (
      task.taskId === taskId ? { ...task, ...remPatch } : task
    )));

    setSelectedRows((prev) => {
      const isSelected = prev.some((task) => task.taskId === taskId);
      if (!isSelected) {
        return prev;
      }
      return prev.map((task) => (
        task.taskId === taskId ? { ...task, ...remPatch } : task
      ));
    });
  };

  const handlePriorityChange = (priorityObj, taskId) => {
    if (priorityObj) {
      persistTaskPatch(taskId, {
        priorityId: priorityObj.value,
        priorityName: priorityObj.label,
      }, {
        priorityId: priorityObj.value,
        taskId: taskId,
      });
    }
  };

  const handleStatusChange = (statusObj, taskId) => {
    if (statusObj) {
      persistTaskPatch(taskId, {
        taskStatusId: statusObj.value,
        statusName: statusObj.label,
      }, {
        TaskStatusId: statusObj.value,
        taskId: taskId,
      });
    }
  };

  const handleTaskTypeChange = (selectedType, taskId) => {
    if (selectedType) {
      persistTaskPatch(taskId, {
        taskTypeId: selectedType.value,
        taskTypeName: selectedType.label,
      }, {
        taskTypeId: selectedType.value,
        taskId: taskId,
      });
    }
  }

  const selectedAssigneeObj = (task) => {
    const userId = task?.assignedTo;
    const assignee = userList?.find(item => String(item.value) === String(userId));

    if (assignee) {
      return assignee;
    }

    if (task?.assigneeName) {
      return {
        value: userId,
        label: task.assigneeName,
      };
    }

    return null;
  }

  const handleChangeAssignee = (newValue, taskId) => {
    if (newValue) {
      persistTaskPatch(taskId, {
        assignedTo: newValue.value,
        assigneeName: newValue.label,
      }, {
        assignedTo: newValue.value,
        taskId: taskId,
      });
      setCloseOnSelect(!closeOnSelect);
    }
  };

  const handleRowSelect = (task) => {
    setSelectedRows((prev) =>
      prev.some((t) => t.taskId === task.taskId)
        ? prev.filter((t) => t.taskId !== task.taskId) : [...prev, task]
    );
  };

  const handleHeaderCheckbox = (groupTasks) => {
    const groupIdSet = new Set(groupTasks.map((task) => task.taskId));
    const allSelected = groupTasks.every((task) => selectedTaskIdSet.has(task.taskId));

    if (allSelected) {
      setSelectedRows(selectedRows.filter((t) => !groupIdSet.has(t.taskId)));
    } else {
      setSelectedRows([
        ...selectedRows,
        ...groupTasks.filter((gt) => !selectedTaskIdSet.has(gt.taskId)),
      ]);
    }
  };

  const handleClearSelection = () => {
    setSelectedRows([]);
  };

  const selectedCount = selectedRows.length;
  const selectionExceedsCap = selectedCount > BULK_SELECTION_CAP;
  const bulkControlsDisabled = isBulkApplying || selectionExceedsCap || selectedCount === 0;

  useEffect(() => {
    if (selectionExceedsCap && !exceededBulkCapRef.current) {
      ToastService.warning(BULK_SELECTION_CAP_MESSAGE);
    }
    exceededBulkCapRef.current = selectionExceedsCap;
  }, [selectionExceedsCap]);

  const getSelectedTaskIds = () => selectedRows.map((task) => task.taskId);

  const toastBulkResult = (actionLabel, successCount, failedCount, totalCount) => {
    if (failedCount === 0) {
      ToastService.success(`${actionLabel} ${successCount} task${successCount === 1 ? "" : "s"}`);
      return;
    }
    if (successCount === 0) {
      ToastService.error(`Could not ${actionLabel.toLowerCase()} selected tasks`);
      return;
    }
    ToastService.warning(
      `${actionLabel} ${successCount} of ${totalCount}; ${failedCount} failed`
    );
  };

  const ensureBulkSelectionAllowed = () => {
    if (!isBulkSelectionMode) {
      return false;
    }
    if (selectedCount === 0) {
      return false;
    }
    if (selectionExceedsCap) {
      ToastService.warning(BULK_SELECTION_CAP_MESSAGE);
      return false;
    }
    return true;
  };

  const applyBulkFieldUpdate = async ({ patchBuilder, requestFields, actionLabel }) => {
    if (!ensureBulkSelectionAllowed()) return;

    const selectedIds = getSelectedTaskIds();
    const confirmed = await confirm(
      "Update selected tasks?",
      `Update ${selectedIds.length} task${selectedIds.length === 1 ? "" : "s"}?`,
      "Update",
      "Cancel"
    );

    if (!confirmed) return;

    const selectedIdSet = new Set(selectedIds);
    const previousById = new Map(
      selectedRows.map((task) => [task.taskId, task])
    );

    setTaskList((prev) =>
      prev.map((task) => {
        if (!previousById.has(task.taskId)) return task;
        return { ...task, ...patchBuilder(task) };
      })
    );
    setIsBulkApplying(true);

    try {
      const result = await bulkUpdateTasks({
        taskIds: selectedIds,
        ...requestFields,
      }).unwrap();

      const failedIds = new Set(
        (result?.failedTasks || []).map((item) => item.taskId)
      );
      const successCount = result?.successCount ?? (selectedIds.length - failedIds.size);
      const failedCount = failedIds.size;

      setTaskList((prev) => {
        const nextList = prev.map((task) => {
          if (!selectedIdSet.has(task.taskId)) return task;
          if (failedIds.has(task.taskId) && previousById.has(task.taskId)) {
            return previousById.get(task.taskId);
          }
          const previous = previousById.get(task.taskId);
          return previous ? { ...previous, ...patchBuilder(previous) } : task;
        });
        setSelectedRows(nextList.filter((task) => selectedIdSet.has(task.taskId)));
        return nextList;
      });

      toastBulkResult(actionLabel, successCount, failedCount, selectedIds.length);
    } catch (error) {
      setTaskList((prev) =>
        prev.map((task) =>
          previousById.has(task.taskId) ? previousById.get(task.taskId) : task
        )
      );
      setSelectedRows(Array.from(previousById.values()));
      ToastService.error(`Could not ${actionLabel.toLowerCase()} selected tasks`);
    } finally {
      setIsBulkApplying(false);
    }
  };

  const handleBulkTypeChange = (selectedType) => {
    if (!selectedType) return;

    applyBulkFieldUpdate({
      actionLabel: "Updated",
      requestFields: { taskTypeId: selectedType.value },
      patchBuilder: () => ({
        taskTypeId: selectedType.value,
        taskTypeName: selectedType.label,
      }),
    });
  };

  const handleBulkStatusChange = (selectedStatus) => {
    if (!selectedStatus) return;

    applyBulkFieldUpdate({
      actionLabel: "Updated",
      requestFields: { taskStatusId: selectedStatus.value },
      patchBuilder: () => ({
        taskStatusId: selectedStatus.value,
        statusName: selectedStatus.label,
      }),
    });
  };

  const handleBulkPriorityChange = (selectedPriority) => {
    if (!selectedPriority) return;

    applyBulkFieldUpdate({
      actionLabel: "Updated",
      requestFields: { priorityId: selectedPriority.value },
      patchBuilder: () => ({
        priorityId: selectedPriority.value,
        priorityName: selectedPriority.label,
      }),
    });
  };

  const handleBulkAssigneeChange = (selectedAssignee) => {
    if (!selectedAssignee) return;

    applyBulkFieldUpdate({
      actionLabel: "Updated",
      requestFields: { assignedTo: selectedAssignee.value },
      patchBuilder: () => ({
        assignedTo: selectedAssignee.value,
        assigneeName: selectedAssignee.label,
      }),
    });
  };

  const handleBulkEstimateApply = (estimateValue) => {
    const nextHours = Number(estimateValue);
    if (!Number.isFinite(nextHours) || nextHours < 0) {
      ToastService.error("Enter a valid estimate of 0 or more");
      return;
    }

    applyBulkFieldUpdate({
      actionLabel: "Updated",
      requestFields: { estimateHours: nextHours },
      patchBuilder: () => ({ estimateHours: nextHours }),
    });
  };

  const handleBulkDeadlineApply = (deadlineValue) => {
    if (!deadlineValue) {
      ToastService.error("Select a deadline");
      return;
    }

    const deadlineDate = new Date(`${deadlineValue}T00:00:00`);
    if (Number.isNaN(deadlineDate.getTime())) {
      ToastService.error("Select a valid deadline");
      return;
    }

    applyBulkFieldUpdate({
      actionLabel: "Updated",
      requestFields: { deadLine: deadlineDate },
      patchBuilder: () => ({ deadLine: deadlineDate.toISOString() }),
    });
  };

  const handleBulkDelete = () => {
    if (!ensureBulkSelectionAllowed()) return;

    const selectedIds = getSelectedTaskIds();
    confirm(
      "Delete?",
      `Delete ${selectedIds.length} task${selectedIds.length === 1 ? "" : "s"}?`,
      "Delete",
      "Cancel"
    ).then(async (confirmed) => {
      if (!confirmed) return;

      const previousList = taskList;
      const previousSelected = selectedRows;
      const idSet = new Set(selectedIds);

      setTaskList((prev) => prev.filter((task) => !idSet.has(task.taskId)));
      setIsBulkApplying(true);

      try {
        const result = await bulkDeleteTasks({ taskIds: selectedIds }).unwrap();
        const failedIds = new Set(
          (result?.failedTasks || []).map((item) => item.taskId)
        );
        const successCount = result?.successCount ?? (selectedIds.length - failedIds.size);
        const failedCount = failedIds.size;

        if (failedCount > 0) {
          const restoreTasks = previousList.filter((task) => failedIds.has(task.taskId));
          setTaskList((prev) => {
            const existingIds = new Set(prev.map((task) => task.taskId));
            return [...prev, ...restoreTasks.filter((task) => !existingIds.has(task.taskId))];
          });
        }

        setSelectedRows((prev) => prev.filter((task) => failedIds.has(task.taskId)));
        toastBulkResult("Deleted", successCount, failedCount, selectedIds.length);
      } catch (error) {
        setTaskList(previousList);
        setSelectedRows(previousSelected);
        ToastService.error("Could not delete selected tasks");
      } finally {
        setIsBulkApplying(false);
      }
    });
  };

  const manageTaskOverDue = (deadlineTime) => {
    if (deadlineTime) {
      const now = new Date();
      const deadline = new Date(deadlineTime);

      now.setHours(0, 0, 0, 0);
      deadline.setHours(0, 0, 0, 0);

      if (deadline < now) {
        return "is-overdue";
      } else if (deadline.getTime() === now.getTime()) {
        return "is-today";
      }
    }
    return "";
  };

  const calculateTimeOverDue = (actual, estimated) => {
    if (!estimated || estimated <= 0) return '';

    const difference = actual - estimated;
    const percentOver = (difference / estimated) * 100;

    if (percentOver > 10) {
      return 'time-overdue';
    }

    return '';
  }

  const getRowClassName = (task) => {
    const classes = [];
    if (selectedTaskIdSet.has(task.taskId)) classes.push("active");
    if (savingTaskIds.has(task.taskId)) classes.push("is-saving");
    return classes.join(" ");
  };

  if (isLoading) {
    return <TaskGridSkeleton
      isShowActualTime={isShowActualTime}
      isShowRemainingTime={isShowRemainingTime}
      isGrouped={isGrouped}
    />;
  }

  const emptyStateMessage = hasActiveFilters
    ? "No tasks match your filters"
    : hasActiveSearch
      ? "No tasks match your search"
      : "No tasks yet";

  return (
    <div className={`task-grid-container ${isGrouped ? "is-grouped" : ""}`} ref={containerRef}>
      <div className="task-grid-scroll">
        <div className="expand-collapse-btn-container" ref={toolbarRef}>
          <div className="task-grid-toolbar-row">
            <div className="task-grid-toolbar-left">
              <div className="task-grid-result-count">
                {totalCount != null
                  ? `Showing ${taskList.length} of ${totalCount} task${totalCount === 1 ? "" : "s"}`
                  : `Showing ${taskList.length} task${taskList.length === 1 ? "" : "s"}`}
              </div>
              {isBulkSelectionMode && taskList.length > 0 && (
                <TaskGridBulkBar
                  selectedCount={selectedCount}
                  isBulkApplying={isBulkApplying}
                  allowEditStatus={allowEditStatus}
                  allowEditAssignee={allowEditAssignee}
                  taskTypeList={taskTypeList}
                  statusList={statusList}
                  priorityList={priorityList}
                  userList={userList}
                  disabled={bulkControlsDisabled}
                  onClearSelection={handleClearSelection}
                  onTypeSelect={handleBulkTypeChange}
                  onStatusSelect={handleBulkStatusChange}
                  onPrioritySelect={handleBulkPriorityChange}
                  onAssigneeSelect={handleBulkAssigneeChange}
                  onEstimateApply={handleBulkEstimateApply}
                  onDeadlineApply={handleBulkDeadlineApply}
                />
              )}
            </div>
            <div className="expand-collapse-btn-actions">
              {isBulkSelectionMode && taskList.length > 0 && (
                <button
                  type="button"
                  className="toolbar-action-btn toolbar-delete-btn"
                  disabled={bulkControlsDisabled}
                  onClick={handleBulkDelete}
                >
                  <Iconify icon="mdi:trash-can-outline" width={16} />
                  Delete
                </button>
              )}
              <Buttons
                buttonTypeClassName={"refresh-btn"}
                onClick={onRefreshGrid}
                buttonText={""}
                textWithIcon={true}
                fontIcon={true}
                iconName={"ic:sharp-refresh"}
                titleText={'Refresh'}
                isLoading={isRefreshing}
              />
              {isGrouped && taskList.length > 0 && (
                <button
                  type="button"
                  className="toolbar-action-btn expand-collapse-toggle-btn"
                  onClick={toggleAllGroups}
                >
                  <Iconify
                    icon={allExpanded ? "mdi:collapse-all-outline" : "mdi:expand-all-outline"}
                    width={16}
                  />
                  {allExpanded ? "Collapse all" : "Expand all"}
                </button>
              )}
            </div>
          </div>
          {taskList.length > 0 && (
            <div className="task-grid-toolbar-row task-grid-toolbar-row--stats">
              <TaskGridStats {...overallStats} />
            </div>
          )}
        </div>
        {isError && (
          <div className="task-grid-error-banner">
            <span>Could not load tasks.</span>
            {onRetry && (
              <button type="button" className="btn btn-primary" onClick={onRetry}>
                Retry
              </button>
            )}
          </div>
        )}
        {!isError && taskList.length === 0 && (
          <div className="task-grid-empty-state">
            <NoDataFound message={emptyStateMessage} />
            {hasActiveFilters && onClearFilters && (
              <button type="button" className="btn btn-primary" onClick={onClearFilters}>
                Clear filters
              </button>
            )}
          </div>
        )}
        {taskList.length > 0 &&
          Object.entries(updatedGroupedTaskList).map(([groupKey, groupData]) => {

            const { tasks, totalEstHours, totalTask, totalBug } = groupData;

            return (
              <div className="task-group" key={groupKey}>
                {isGrouped && (
                  <div className="group-header" onClick={() => toggleGroupCollapse(groupKey)}>
                    <div className="group-header-left">
                      <span className="group-header-checkbox-spacer" aria-hidden="true" />
                      <button type="button" className="collapse-btn">
                        <Iconify
                          icon={
                            collapsedGroups[groupKey]
                              ? "material-symbols:expand-more"
                              : "material-symbols:chevron-right"
                          }
                          width={20}
                        />
                        <strong>{groupKey}</strong>
                      </button>
                    </div>

                    <TaskGridStats
                      totalEstHours={totalEstHours}
                      totalTask={totalTask}
                      totalBug={totalBug}
                    />
                  </div>
                )}

                {(isGrouped ? collapsedGroups[groupKey] : true) && (
                  <table className="task-table">
                    <thead>
                      <tr>
                        {gridColumns.map((column) => {
                          if (column.key === "checkbox") {
                            return (
                              <th key={column.key} className={column.className}>
                                <Checkbox
                                  size="small"
                                  checked={tasks.every((task) => selectedTaskIdSet.has(task.taskId))}
                                  indeterminate={tasks.some((task) => selectedTaskIdSet.has(task.taskId))}
                                  onChange={() => handleHeaderCheckbox(tasks)}
                                />
                              </th>
                            );
                          }

                          const isSorted = isSortedByColumn(column);

                          return (
                            <th
                              key={column.key}
                              className={column.className}
                              aria-sort={
                                isSorted
                                  ? (sortDirection === "ASC" ? "ascending" : "descending")
                                  : undefined
                              }
                            >
                              {column.sortKey ? (
                                <button
                                  type="button"
                                  className={`task-grid-sort-btn ${isSorted ? "is-sorted" : ""}`}
                                  title={`Sort by ${column.sortLabel}`}
                                  onClick={() => handleColumnSort(column)}
                                >
                                  <span>{column.label}</span>
                                  <Iconify
                                    icon={
                                      isSorted
                                        ? (sortDirection === "ASC" ? "mdi:arrow-up" : "mdi:arrow-down")
                                        : "mdi:unfold-more-horizontal"
                                    }
                                    width={14}
                                  />
                                </button>
                              ) : (
                                column.label
                              )}
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {tasks.map((task) => {
                        return (
                          <tr
                            key={task.taskId}
                            className={getRowClassName(task)}
                          >
                            <td className="taskgrid-checkbox-column">
                              <Checkbox
                                size="small"
                                checked={selectedTaskIdSet.has(task.taskId)}
                                onChange={() => handleRowSelect(task)}
                              />
                            </td>

                            <td className="taskgrid-task-type-column">
                              <TaskTypeSelect
                                task={task}
                                taskTypeLabel={task.taskTypeId}
                                taskTypeList={taskTypeList}
                                onTaskNavigate={handleTaskNavigate}
                                onChange={(value) => handleTaskTypeChange(value, task.taskId)}
                              />
                              <div className="copy-button">
                                <IconButton
                                  icon="material-symbols-light:content-copy-rounded"
                                  size="small"
                                  className="copy-icon"
                                  title="Copy task details"
                                  aria-label="Copy task details"
                                  onClick={() => handleTaskCopyClick(task)}
                                  shape="square"
                                  variant="outlined"
                                />
                              </div>
                            </td>

                            <td
                              onClick={() => handleTaskEditClick(task, "title")}
                              className={`editable-task taskgrid-title-column ${editingTaskId === task.taskId ? "active" : ""}`}
                            >
                              {editingTaskId === task.taskId ? (
                                <input
                                  ref={taskInputRef}
                                  type="text"
                                  value={taskTitle}
                                  onChange={handleTaskTitleChange}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      e.preventDefault();
                                      skipTitleBlurRef.current = true;
                                      handleSaveTaskTitle(task.taskId);
                                    } else if (e.key === "Escape") {
                                      e.preventDefault();
                                      skipTitleBlurRef.current = true;
                                      cancelTitleEdit();
                                    }
                                  }}
                                  onBlur={() => {
                                    if (skipTitleBlurRef.current) {
                                      skipTitleBlurRef.current = false;
                                      return;
                                    }
                                    if (taskTitle !== task.title) {
                                      handleSaveTaskTitle(task.taskId)
                                    } else {
                                      cancelTitleEdit();
                                    }
                                  }}
                                  className="task-input"
                                />
                              ) : (
                                <div className="task-name-container">
                                  <span title={task.title}>{task.title}</span>
                                  <div className="edit-task-button">
                                    <IconButton
                                      icon="material-symbols:edit"
                                      size="small"
                                      className="edit-task-icon"
                                      title="Edit title"
                                      aria-label="Edit title"
                                      onClick={() => handleTaskEditClick(task, "title")}
                                      shape="square"
                                      variant="outlined"
                                    />
                                  </div>
                                </div>
                              )}
                            </td>

                            <td
                              onClick={() => handleTaskEditClick(task, "time")}
                              className={`editable-time taskgrid-est-column ${editingTaskTimeId === task.taskId ? "active" : ""}`}
                            >
                              {editingTaskTimeId === task.taskId ? (
                                <input
                                  ref={timeInputRef}
                                  type="number"
                                  step="0.1"
                                  min="0"
                                  value={taskEstTime}
                                  onChange={handleTaskTimeChange}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      e.preventDefault();
                                      skipTimeBlurRef.current = true;
                                      handleSaveTaskTime(task.taskId);
                                    } else if (e.key === "Escape") {
                                      e.preventDefault();
                                      skipTimeBlurRef.current = true;
                                      cancelTimeEdit();
                                    }
                                  }}
                                  onBlur={() => {
                                    if (skipTimeBlurRef.current) {
                                      skipTimeBlurRef.current = false;
                                      return;
                                    }
                                    if (Number(task.estimateHours) !== Number(taskEstTime)) {
                                      handleSaveTaskTime(task.taskId)
                                    } else {
                                      cancelTimeEdit();
                                    }
                                  }}
                                  className="task-input"
                                />
                              ) : (
                                <div className="time-container">
                                  <span>{task.estimateHours} hrs</span>
                                  <div className="edit-time-button">
                                    <IconButton
                                      icon="material-symbols:edit"
                                      size="small"
                                      className="edit-time-icon"
                                      title="Edit estimate"
                                      aria-label="Edit estimate"
                                      onClick={() => handleTaskEditClick(task, "time")}
                                      shape="square"
                                      variant="outlined"
                                    />
                                  </div>
                                </div>
                              )}
                            </td>

                            {isShowRemainingTime && (
                              <td
                                onClick={canEditRemainingTime ? () => handleTaskEditClick(task, "remTime") : undefined}
                                className={`taskgrid-rem-column ${canEditRemainingTime ? "editable-time" : ""} ${editingTaskRemId === task.taskId ? "active" : ""}`}
                              >
                                {canEditRemainingTime && editingTaskRemId === task.taskId ? (
                                  <input
                                    ref={remTimeInputRef}
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    value={taskRemTime}
                                    onChange={handleTaskRemTimeChange}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        e.preventDefault();
                                        skipRemTimeBlurRef.current = true;
                                        handleSaveTaskRemTime(task.taskId);
                                      } else if (e.key === "Escape") {
                                        e.preventDefault();
                                        skipRemTimeBlurRef.current = true;
                                        cancelRemTimeEdit();
                                      }
                                    }}
                                    onBlur={() => {
                                      if (skipRemTimeBlurRef.current) {
                                        skipRemTimeBlurRef.current = false;
                                        return;
                                      }
                                      const currentHours = Number(task.remTime ?? task.remainingHours ?? 0);
                                      if (currentHours !== Number(taskRemTime)) {
                                        handleSaveTaskRemTime(task.taskId);
                                      } else {
                                        cancelRemTimeEdit();
                                      }
                                    }}
                                    className="task-input"
                                  />
                                ) : (
                                  <div className="time-container">
                                    <span>{task.remTime ?? task.remainingHours ?? 0} hrs</span>
                                    {canEditRemainingTime && (
                                      <div className="edit-time-button">
                                        <IconButton
                                          icon="material-symbols:edit"
                                          size="small"
                                          className="edit-time-icon"
                                          title="Edit remaining time"
                                          aria-label="Edit remaining time"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleTaskEditClick(task, "remTime");
                                          }}
                                          shape="square"
                                          variant="outlined"
                                        />
                                      </div>
                                    )}
                                  </div>
                                )}
                              </td>
                            )}

                            {isShowActualTime && (
                              <td className={`taskgrid-actual-hours-column ${calculateTimeOverDue(task.actualHours, task.estimateHours)}`}>
                                <div className="actual-time-container">
                                  <span>{formatActualHours(task.actualHours)}</span>
                                </div>
                              </td>)}

                            <td className="taskgrid-priority-column">
                              <PrioritySelect
                                key={task.taskId}
                                priorityList={priorityList}
                                selectedPriority={task.priorityId}
                                onChange={(priority) => handlePriorityChange(priority, task.taskId)}
                              />
                            </td>

                            <td className={`taskgrid-deadline-column ${manageTaskOverDue(task.deadLine)}`}>
                              <div className='deadline-container'>
                                <Iconify icon="mdi:calendar-blank-outline" width={14} />
                                <span>{task.deadLine ? formatDate(task.deadLine, "DD/MM/YYYY") : "-"}</span>
                              </div>
                            </td>

                            <td className="taskgrid-status-column">
                              <StatusSelect
                                projectId={projectId}
                                statusList={statusList}
                                allowEditStatus={allowEditStatus}
                                selectedStatus={task.taskStatusId}
                                selectedStatusName={task.statusName}
                                selectedStatusClassName={task.statusCategoryName}
                                onChange={(status) => handleStatusChange(status, task.taskId)}
                              />
                            </td>

                            <td className="taskgrid-assignee-avatar-column">
                              <AssigneeSelect
                                selectedAssignee={selectedAssigneeObj(task)}
                                assignees={userList}
                                allowEdit={allowEditAssignee}
                                onChangeAssignee={(assignee) => handleChangeAssignee(assignee, task.taskId)}
                                closeOnSelect={closeOnSelect}
                                size="small"
                                shape="circle"
                                position="right"
                              />
                            </td>

                            <td className="taskgrid-action-column">
                              <div className="taskgrid-action-btns">
                                <button type="button" className="edit-btn" title="Open task" aria-label="Open task" onClick={() => handleTaskNavigate(task)}>
                                  <Iconify icon="line-md:pencil" width={16} />
                                </button>
                                {isTeamPlanning && onRemoveTask && (
                                  <button type="button" className="remove-btn" title="Remove from planning" aria-label="Remove from planning" onClick={() => handleRemoveTaskFromPlanning(task)}>
                                    <Iconify icon="carbon:task-remove" width={16} />
                                  </button>
                                )}
                                <button type="button" className="delete-btn" title="Delete task" aria-label="Delete task" onClick={() => handleDeleteTask(task)}>
                                  <Iconify icon="mingcute:delete-2-line" width={16} />
                                </button>
                                {customGridActions?.length ? customGridActions.map((action, index) => {
                                  return (
                                    <button
                                      type="button"
                                      key={index + 1}
                                      title={action.actionName}
                                      aria-label={action.actionName}
                                      className={`custom-action-btn ${action.actionClassName || 'other-btn'}`}
                                      onClick={() => handleCustomActionClick(action.actionName, task)}
                                    >
                                      <Iconify icon={action.iconClass || "carbon:overflow-menu-horizontal"} width={16} />
                                    </button>
                                  )
                                }) : null}
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )
          })
        }
      </div>
    </div>
  );
};

export default NewTaskGrid;
